<template>
  <Modal
      :visible="props.visible"
      title="测试区域与路线选择"
      width="900px"
      @cancel="handleCancel"
      @close="handleClose"
      footer=""
  >
    <div class="test-area-select-container">
      <!-- 左侧测试区域选择 -->
      <div class="left-panel">
        <div class="panel-title">
          测试区域选择
        </div>
        <List
            :data-source="openAreaRoutes"
            class="area-list"
        >
          <template #renderItem="{ item }">
            <List.Item
                :class="['area-item']"
                @click="handleAreaSelect(item)"
            >
              <span>{{ item.areaName }}</span>
              <span class="route-count">
                {{ getSelectedRouteCount(item.areaId) }}/{{ item.routes ? item.routes.length : 0 }}
              </span>
            </List.Item>
          </template>
        </List>
      </div>

      <!-- 右侧测试路线选择 -->
      <div class="right-panel">
        <div class="panel-title">
          {{ currentArea ? `${currentArea.areaName} - 测试路线 ` : '请先选择测试区域' }}
        </div>
        <div class="routes-container" v-if="currentArea && currentArea.routes && currentArea.routes.length > 0">
          <List
              :data-source="currentArea.routes"
              item-layout="horizontal"
          >
            <template #renderItem="{ item }">
              <List.Item
                  :class="['route-item', { 'selected': selectedRoutes.some(r => r.routeId === item.routeId) }]"
                  @click="handleRouteSelect(item)"
                  extra=""
              >
                <div style="flex: 1; min-width: 0;">
                  <List.Item.Meta
                      :title="item.name"
                      :description="''"
                  />
                  <div class="route-info">
                    <span class="info-item">距离: {{ item.distance }}公里</span>
                    <span class="info-item">限速: {{ item.limitSpeed }}km/h</span>
                  </div>
                </div>
                <div class="check-icon-container">
                  <CheckOutlined v-if="selectedRoutes.some(r => r.routeId === item.routeId)" class="selected-icon" />
                </div>
              </List.Item>
            </template>
          </List>
        </div>
        <div v-else-if="currentArea" class="no-routes">
          该测试区域暂无可用测试路线
        </div>
        <div v-else class="no-routes">
          请先选择测试区域
        </div>
      </div>
    </div>

    <!-- 底部按钮 -->
    <div class="modal-footer">
      <Button @click="handleCancel">取消</Button>
      <Button type="primary" @click="handleConfirm">确认选择</Button>
    </div>
  </Modal>
</template>

<script setup>
import { ref, defineProps, defineEmits, onMounted, watch } from 'vue';
import { Modal, List, Button, message } from 'ant-design-vue';
import { CheckOutlined } from '@ant-design/icons-vue';
import { getRoutes } from '@/api/roads/routes'

// 定义组件属性
const props = defineProps({
  // 控制弹框显示/隐藏
  visible: {
    type: Boolean,
    default: false
  },
  // 标题
  categoryCode: {
    type: String,
    required: true
  },
  // 已选中的测试路线（从父组件传入）
  selectedRoutes: {
    type: Array,
    default: () => []
  }
});

// 定义事件
const emit = defineEmits([
  // 关闭弹框事件
  'close',
  // 确认选择事件
  'confirm',
  // 取消选择事件
  'cancel',
  // v-model更新事件
  'update:visible'
]);

// 测试区域数据
const openAreaRoutes = ref([]);
// 选中的测试区域（支持多选）
const selectedAreas = ref([]);
// 当前查看的区域
const currentArea = ref(null);
// 选中的测试路线（支持多选）
const selectedRoutes = ref([]);

// 初始化模拟数据
const initAreaRoutesData = () => {

  getRoutes(props.categoryCode).then(res => {
        // 检查响应是否成功并包含数据
        if (res && res.data) {
          openAreaRoutes.value = res.data;
          // 重置选中状态
          selectedAreas.value = [];
          currentArea.value = null;
          selectedRoutes.value = [];
          
          // 如果有已选中的路线数据传入，初始化选中状态
          if (props.selectedRoutes && props.selectedRoutes.length > 0) {
            // 遍历所有区域和路线，找到匹配的路线并设置选中状态
            openAreaRoutes.value.forEach(area => {
              if (area.routes && area.routes.length > 0) {
                area.routes.forEach(route => {
                  // 尝试多种匹配方式，确保类型正确转换
                  const matchedRoute = props.selectedRoutes.find(r => 
                    String(r.routeId) === String(route.routeId) || 
                    String(r.id) === String(route.routeId)
                  );
                  
                  if (matchedRoute) {
                    // 添加到选中路线列表
                    const routeWithArea = {
                      ...route,
                      areaName: area.areaName,
                      areaId: area.areaId
                    };
                    selectedRoutes.value.push(routeWithArea);
                    
                    // 添加对应的区域到选中区域列表
                    if (!selectedAreas.value.some(z => z.areaId === area.areaId)) {
                      selectedAreas.value.push({...area});
                    }
                    
                    // 设置第一个选中的区域为当前查看区域
                    if (!currentArea.value) {
                      currentArea.value = {...area};
                    }
                  }
                });
              }
            });
          }
        }
      }).catch(error => {
        console.error('请求测试区域数据出错:', error);
      })
};

// 计算指定区域已选择的路线数量
const getSelectedRouteCount = (areaId) => {
  return selectedRoutes.value.filter(route => route.areaId === areaId).length;
};

// 处理测试区域选择
const handleAreaSelect = (area) => {

  const fullArea = { ...area };
  // 检查区域是否已选中
  const index = selectedAreas.value.findIndex(z => z.areaId === fullArea.areaId);

  if (index > -1) {
    // 如果已选中，不取消选中，只更新当前查看的区域
    currentArea.value = fullArea;
    // 不从选中的路线中移除该区域的路线
  } else {
    // 如果未选中，则添加到选中列表
    selectedAreas.value.push(fullArea);
    // 设置为当前查看的区域
    currentArea.value = fullArea;
  }
};

// 处理测试路线选择
const handleRouteSelect = (route) => {
  // 检查路线是否已选中
  const index = selectedRoutes.value.findIndex(r => r.routeId === route.routeId);
  
  if (index > -1) {
    // 如果已选中，则取消选中
    selectedRoutes.value.splice(index, 1);
  } else {
    // 如果未选中，则添加到选中列表，并关联所属区域信息
    const routeWithArea = {
      ...route,
      areaName: currentArea.value ? currentArea.value.areaName : '',
      areaId: currentArea.value ? currentArea.value.areaId : ''
    };
    selectedRoutes.value.push(routeWithArea);
  }
};

// 处理取消
const handleCancel = () => {
  emit('cancel');
  emit('update:visible', false);
};

// 处理关闭
const handleClose = () => {
  emit('close');
  emit('update:visible', false);
};

// 处理确认选择
const handleConfirm = () => {
  if (selectedAreas.value.length === 0) {
    message.warning('请至少选择一个测试区域');
    return;
  }
  if (selectedRoutes.value.length === 0) {
    message.warning('请至少选择一条测试路线');
    return;
  }

  // 触发确认事件，传递选择的区域和路线信息
  emit('confirm', {
    areas: selectedAreas.value,
    routes: selectedRoutes.value
  });

  // 确认后关闭弹框
  emit('update:visible', false);
};

// 监听visible变化，当弹框打开时重新初始化数据
watch(() => props.visible, (newVal) => {
  if (newVal) {
    initAreaRoutesData();
  }
});

// 监听已选中路线的变化，及时更新选中状态
watch(() => props.selectedRoutes, (newRoutes) => {
  // 只有当openAreaRoutes已经加载完成且有数据时才更新
  if (openAreaRoutes.value.length > 0 && newRoutes && newRoutes.length > 0) {
    // 重置当前选中状态
    selectedRoutes.value = [];
    selectedAreas.value = [];
    
    // 重新匹配并设置选中状态
    openAreaRoutes.value.forEach(area => {
      if (area.routes && area.routes.length > 0) {
        area.routes.forEach(route => {
          const matchedRoute = newRoutes.find(r => 
            String(r.routeId) === String(route.routeId) || 
            String(r.id) === String(route.routeId)
          );
          
          if (matchedRoute) {
            const routeWithArea = {
              ...route,
              areaName: area.areaName,
              areaId: area.areaId
            };
            selectedRoutes.value.push(routeWithArea);
            
            if (!selectedAreas.value.some(z => z.areaId === area.areaId)) {
              selectedAreas.value.push({...area});
            }
            
            if (!currentArea.value) {
              currentArea.value = {...area};
            }
          }
        });
      }
    });
  }
}, { deep: true });

// 组件挂载时初始化数据
onMounted(() => {
  if (props.visible) {
    initAreaRoutesData();
  }
  
  // 确保即使visible为false时，也能正确初始化选中状态的数据
  // 这样当弹框第一次打开时，就能立即显示正确的选中状态
  if (props.selectedRoutes && props.selectedRoutes.length > 0 && !props.visible) {
    // 先加载区域和路线数据
    getRoutes(props.categoryCode).then(res => {
      if (res && res.data) {
        openAreaRoutes.value = res.data;
      }
    });
  }
});
</script>

<style scoped lang="less">
.test-area-select-container {
  display: flex;
  gap: 20px;
  min-height: 400px;
  max-height: 500px;

  .left-panel {
    width: 300px;
    border: 1px solid #e8e8e8;
    border-radius: 4px;
    display: flex;
    flex-direction: column;

    .panel-title {
      padding: 12px 16px;
      font-weight: 500;
      border-bottom: 1px solid #e8e8e8;
      background-color: #fafafa;
    }

    .area-list {
      flex: 1;
      overflow: auto;

      .area-item {
        cursor: pointer;
        transition: all 0.3s;
        display: flex;
        justify-content: space-between;
        align-items: center;

        &:hover {
          background-color: #f5f5f5;
        }

        .route-count {
          font-size: 14px;
          color: #666;
          min-width: 40px;
          text-align: right;
        }
      }
    }
  }

  .right-panel {
    flex: 1;
    border: 1px solid #e8e8e8;
    border-radius: 4px;
    display: flex;
    flex-direction: column;

    .panel-title {
      padding: 12px 16px;
      font-weight: 500;
      border-bottom: 1px solid #e8e8e8;
      background-color: #fafafa;
    }

    .routes-container {
      flex: 1;
      overflow: auto;

      .route-item {
        cursor: pointer;
        transition: all 0.3s;
        display: flex;
        justify-content: space-between;

        &:hover {
          background-color: #f5f5f5;
        }

        &.selected {
          background-color: #e6f7ff;
          border-right: 3px solid #1890ff;
        }

        .route-info {
          display: flex;
          gap: 20px;
          margin-top: 8px;

          .info-item {
            font-size: 14px;
            color: #666;
          }
        }

        .check-icon-container {
          width: 30px;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .selected-icon {
          color: #1890ff;
          font-size: 18px;
        }
      }
    }

    .no-routes {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #999;
      font-size: 14px;
    }
  }
}

.modal-footer {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid #e8e8e8;
}
</style>